// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  UserService,
  UserServiceRequest,
} from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Service/User';
import { AsyncButton } from '../../components/Button/AsyncButton';
import Loading from '../../components/Loading';
import { NotificationBadge } from '../../components/NotificationBadge';
import PartyDisplay from '../../components/PartyDisplay';
import { DataTable } from '../../components/Table/DataTable';
import { useParties } from '../../contexts/PartiesContext';
import { useAcceptUserServiceRequest } from '../../hooks/mutations/credential/onboarding/useAcceptUserServiceRequest';
import { useCancelUserServiceRequest } from '../../hooks/mutations/credential/onboarding/useCancelUserServiceRequest';
import { useRejectUserServiceRequest } from '../../hooks/mutations/credential/onboarding/useRejectUserServiceRequest';
import { useCreateUserServiceRequest } from '../../hooks/mutations/credential/onboarding/useRequestUserService';
import { useTerminateUserService } from '../../hooks/mutations/credential/onboarding/useTerminateUserService';
import { useCredentialUserService } from '../../hooks/queries/credential/onboarding/useUserService';
import { useUserServiceRequests } from '../../hooks/queries/credential/onboarding/useUserServiceRequests';
import useUserServices from '../../hooks/queries/credential/onboarding/useUserServices';
import TemplateContract from '../../utils/TemplateContract';
import { PageWrapper } from '../PageWrapper';

type ServiceRow = TemplateContract<UserService>;

type RequestRow = TemplateContract<UserServiceRequest>;

type ServiceAction = {
  name: string;
  action: (r: ServiceRow) => Promise<unknown>;
};

type RequestAction = {
  name: string;
  action: (r: RequestRow) => Promise<unknown>;
};

const createServiceHeaders: (
  createActions: (r: ServiceRow) => ServiceAction[],
) => GridColDef<ServiceRow>[] = (createActions) => {
  const renderActionCell = (params: GridRenderCellParams<ServiceRow>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.operator,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.payload.user} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.user,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const createRequestHeaders: (
  createActions: (r: RequestRow) => RequestAction[],
) => GridColDef<RequestRow>[] = (createActions) => {
  const renderActionCell = (params: GridRenderCellParams<RequestRow>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.operator,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.payload.user} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.payload.user,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

export const Onboarding = () => {
  const { party, operator, dso } = useParties();
  const [tab, setTab] = useState(0);
  const onChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const userServices = useUserServices();
  const userServiceRequests = useUserServiceRequests();
  const terminateUserService = useTerminateUserService();
  const approveUserServiceRequest = useAcceptUserServiceRequest();
  const rejectUserServiceRequest = useRejectUserServiceRequest();
  const cancelUserServiceRequest = useCancelUserServiceRequest();
  const createUserServiceRequest = useCreateUserServiceRequest();
  const userService = useCredentialUserService();

  const isLoading = userServices.isLoading || userServiceRequests.isLoading;
  const request = useCallback(async () => {
    await createUserServiceRequest({
      argument: {
        operator,
        user: party,
      },
    });
  }, [createUserServiceRequest, operator, party]);
  if (isLoading) return <Loading />;
  const userServiceRequest = userServiceRequests.data?.find((r) => r.payload.user === party);
  const canRequest = !userService.data && !userServiceRequest; // TODO: Make this more robust
  const terminate = async (r: ServiceRow) => {
    await terminateUserService({
      cid: r.contractId,
      argument: { actor: party },
    });
  };

  const approve = async (r: RequestRow) => {
    await approveUserServiceRequest({
      cid: r.contractId,
      argument: { dso },
    });
  };

  const reject = async (r: RequestRow) => {
    await rejectUserServiceRequest({
      cid: r.contractId,
      argument: {},
    });
  };

  const cancel = async (r: RequestRow) => {
    await cancelUserServiceRequest({
      cid: r.contractId,
      argument: {},
    });
  };

  const createServiceActions = (r: ServiceRow) => {
    const actions = [];
    if (r.payload.operator === party) {
      actions.push({ name: 'Terminate', action: terminate });
    }
    return actions;
  };

  const createRequestActions = (r: RequestRow) => {
    if (r.payload.operator === party)
      return [
        { name: 'Approve', action: approve },
        { name: 'Reject', action: reject },
      ];
    if (r.payload.user === party) return [{ name: 'Cancel', action: cancel }];
    return [];
  };
  const buttons = [
    {
      name: 'Request credential user service',
      onClick: request,
      disabled: !canRequest,
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Onboarding">
      <Tabs value={tab} onChange={onChange}>
        <Tab sx={{ textTransform: 'capitalize' }} value={0} label="Services" />
        <Tab
          value={1}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize', mr: 1 }}>
                Requests
              </Typography>
              <NotificationBadge value={userServiceRequests?.data?.length || 0} />
            </Box>
          }
        />
      </Tabs>
      {tab === 0 && (
        <DataTable
          columns={createServiceHeaders(createServiceActions)}
          rows={userServices.data || []}
          rowKey={(a) => a.contractId}
        />
      )}
      {tab === 1 && (
        <DataTable
          columns={createRequestHeaders(createRequestActions)}
          rows={userServiceRequests.data || []}
          rowKey={(r) => r.contractId}
        />
      )}
    </PageWrapper>
  );
};
